import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {OperationProps} from "../operationProps";
import {useTranslation} from "react-i18next";
import {operationUrl, PRIVATE_URL} from "../../../common/struct/urlManager";
import FileIcon from "../../../assets/file.svg";
import {Box, Button, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useParams} from "react-router-dom";
import {getOperationEmailStats, relaunchParticipants} from "../../../services/OperationService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendEmailToParticipant from "../../operationParticipants/listParticipants/actions/sendEmailToParticipant";
import ListSnackbar, {TAlert} from "../../../patterns/list/listSnackbar";
import {addSuccessAlert, addErrorAlert} from "../../../patterns/list/listUtils";
import {LibertyTheme} from "../../../tokens/libertyTheme";
import {TEmailInvitationSummary} from "../../../interfaces/email";

export default function EmailsContentCard(props: OperationProps): JSX.Element {
  const {t} = useTranslation();
  const {operation} = props;
  const {operationId} = useParams<string>();
  const theme = useTheme();

  const [invitationSummary, setInvitationSummary] = useState<TEmailInvitationSummary>({clicked: 0, received: 0, totalInvited: 0});
  const [relaunchDialogOpen, setRelaunchDialogOpen] = useState(false);
  const [relaunchLoading, setRelaunchLoading] = useState(false);
  const [alerts, setAlerts] = useState<TAlert[]>([]);

  useEffect(() => {
    if (operationId) {
      getOperationEmailStats(operationId)
        .then(stats => {
          setInvitationSummary(stats.invitationSummary);
        })
    }
  }, [operationId]);

  const handleRelaunch = async (templateId: string | null, sendAt: string): Promise<void> => {
    if (!operationId) return;
    setRelaunchLoading(true);
    try {
      const response = await relaunchParticipants(operationId, sendAt, templateId);
      const stats = await getOperationEmailStats(operationId);
      setInvitationSummary(stats.invitationSummary);
      addSuccessAlert(t("operations.customisation.emails.relaunch_success", {count: response.sent.count}), setAlerts);
    } catch (error) {
      addErrorAlert(t("operations.customisation.emails.relaunch_error"), setAlerts);
    } finally {
      setRelaunchLoading(false);
    }
  };

  const total = operation.participantStats.total;
  const invited = invitationSummary.totalInvited;
  const clicked = invitationSummary.clicked;
  const invitedNotClicked = invitationSummary.received;
  const notInvited = Math.max(0, total - invited);
  const relaunchable = Math.max(0, total - clicked);

  return (
    <Box>
      <ListSnackbar alerts={alerts} setAlerts={setAlerts} />
      {total === 0 ? (
        <>
          <Box sx={{border: "1px solid", borderColor: "ornament.dark", borderRadius: "4px"}}>
            <Box sx={{display: "flex", backgroundColor: "ornament.main", borderRadius: "4px", p: 1}}>
              {/** @ts-ignore */}
              <img src={FileIcon} alt="" />
              <Box sx={{display: "flex", flexDirection: "column", ml: 3}}>
                <Typography variant="body2">{t("operations.customisation.emails.empty_state")}</Typography>
              </Box>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={operationUrl(PRIVATE_URL.LIST_OPERATION_PARTICIPANTS, operationId!)}
            sx={{mt: 2, width: "100%"}}
          >
            {t("operations.customisation.emails.empty_state_cta")}
          </Button>
        </>
      ) : (
        <>
          <Box sx={{display: "flex", alignItems: "baseline", mb: 1, gap: 0}}>
            <Typography variant="h5" sx={{fontWeight: 700}} color="primary.main">
              {invited}
            </Typography>
            <Typography variant="body1" color="neutral.main">
              {"/ "}{total} {t("operations.customisation.emails.participants_invited", {count: total})}
            </Typography>
          </Box>

          {/* Barre charte */}
          <Box sx={{display: "flex", height: "12px", borderRadius: "6px", overflow: "hidden", width: "100%"}}>
            {total > 0 ? (
              <>
                <Box sx={{width: `${(clicked / total) * 100}%`, backgroundColor: theme.palette.success.main, transition: "width 0.3s"}} />
                <Box sx={{width: `${(invitedNotClicked / total) * 100}%`, backgroundColor: theme.palette.info.main, transition: "width 0.3s"}} />
                <Box sx={{flex: 1, backgroundColor: theme.palette.warning.dark}} />
              </>
            ) : (
              <Box sx={{width: "100%", backgroundColor: theme.palette.grey[300]}} />
            )}
          </Box>

          {/* Légende */}
          <Box sx={{display: "flex", gap: 2, mt: 1, flexWrap: "wrap"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <Box sx={{width: 10, height: 10, borderRadius: "50%", backgroundColor: LibertyTheme.palette.success.main, flexShrink: 0}} />
              <Typography variant="caption" color="neutral.main">
                {t("operations.customisation.emails.stat_clicked", {count: clicked})}
              </Typography>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <Box sx={{width: 10, height: 10, borderRadius: "50%", backgroundColor: LibertyTheme.palette.info.main, flexShrink: 0}} />
              <Typography variant="caption" color="neutral.main">
                {t("operations.customisation.emails.stat_received", {count: invitedNotClicked})}
              </Typography>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <Box sx={{width: 10, height: 10, borderRadius: "50%", backgroundColor: LibertyTheme.palette.warning.dark, flexShrink: 0, borderColor: "grey.400"}} />
              <Typography variant="caption" color="neutral.main">
                {t("operations.customisation.emails.stat_not_invited", {count: notInvited})}
              </Typography>
            </Box>
          </Box>
          <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 2}}>
            <InfoOutlinedIcon sx={{fontSize: 14, color: "neutral.main"}} />
            <Typography variant="body2" color="neutral.main">
              {t("operations.customisation.emails.stat_relaunchable", {count: relaunchable})}
            </Typography>
          </Box>
          {relaunchable > 0 && (
            <Button
              variant="outlined"
              size="small"
              sx={{mt: 2, width: "100%"}}
              onClick={(): void => setRelaunchDialogOpen(true)}
            >
              {t("operations.customisation.emails.relaunch_cta")}
            </Button>
          )}
          {operationId && (
            <SendEmailToParticipant
              openDialog={relaunchDialogOpen}
              loading={relaunchLoading}
              closeDialog={(): void => setRelaunchDialogOpen(false)}
              sendEmail={handleRelaunch}
              operationParticipant={null}
              operationId={operationId}
              participantCount={relaunchable}
              titleKey="operations.customisation.emails.relaunch_title"
              recipientInfoKey={relaunchable > 1 ? "operations.customisation.emails.relaunch_recipient_info_other" : "operations.customisation.emails.relaunch_recipient_info_one"}
              showTemplateSelection={false}
            />
          )}
        </>
      )}
    </Box>
  );
}
